import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Tab, Tabs, TableRow, TableCell, Typography,
  TextField, useTheme, InputAdornment,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import {
  Sankey, Tooltip as RechartsTooltip, ResponsiveContainer, Layer, Rectangle,
} from 'recharts';
import {
  GlassCard, GlassTable, ShimmerSkeleton, EmptyState,
} from '../../../components/admin/ui';
import { makeAnalyticsApi } from '../../../analytics/adminApi';
import { ADMIN_COLORS } from '../../../components/admin/ui/glassTokens';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDwell(ms) {
  if (!ms) return '0s';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

function pct(v) {
  return v == null ? '—' : `${(v * 100).toFixed(1)}%`;
}

// ─── Custom Sankey node renderer ──────────────────────────────────────────────

function SankeyNode(props) {
  const { x, y, width, height, index, payload } = props;
  const theme = props.theme; // passed via recharts custom
  const isDark = theme?.palette?.mode === 'dark';

  if (!height || height < 1) return null;

  return (
    <Layer key={`CustomNode${index}`}>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={ADMIN_COLORS.accentIndigo}
        fillOpacity={0.85}
        radius={3}
      />
      {height > 14 && (
        <text
          textAnchor="start"
          x={x + width + 6}
          y={y + height / 2}
          fontSize={11}
          fill={isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)'}
          dominantBaseline="middle"
        >
          {payload?.name?.length > 22 ? `${payload.name.slice(0, 20)}…` : payload?.name}
        </text>
      )}
    </Layer>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AnalyticsBehavior({ filters, apiBase = '/admin/analytics' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [subTab, setSubTab] = useState('pages');
  const [pagesLoading, setPagesLoading] = useState(true);
  const [pagesData, setPagesData] = useState([]);
  const [flowLoading, setFlowLoading] = useState(false);
  const [flowData, setFlowData] = useState(null);
  const [entryPath, setEntryPath] = useState('/');
  const [entryInput, setEntryInput] = useState('/');

  // ── Load pages data ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setPagesLoading(true);

    const analyticsApi = makeAnalyticsApi(apiBase);
    analyticsApi.getBehaviorPages({
      from: filters.from,
      to: filters.to,
      limit: 50,
      includeInternalRoles: filters.includeInternalRoles,
      includeBots: filters.includeBots,
    }).then((d) => {
      if (!cancelled) { setPagesData(d.rows || []); setPagesLoading(false); }
    });

    return () => { cancelled = true; };
  }, [filters.from, filters.to, filters.includeInternalRoles, filters.includeBots, apiBase]);

  // ── Load flow data ──────────────────────────────────────────────────────────
  const loadFlow = useCallback(() => {
    let cancelled = false;
    setFlowLoading(true);

    const analyticsApi = makeAnalyticsApi(apiBase);
    analyticsApi.getBehaviorFlow({
      from: filters.from,
      to: filters.to,
      entry: entryPath,
      depth: 5,
      includeInternalRoles: filters.includeInternalRoles,
      includeBots: filters.includeBots,
    }).then((d) => {
      if (!cancelled) { setFlowData(d); setFlowLoading(false); }
    });

    return () => { cancelled = true; };
  }, [filters.from, filters.to, entryPath, filters.includeInternalRoles, filters.includeBots, apiBase]);

  useEffect(() => {
    if (subTab === 'flow') loadFlow();
  }, [subTab, loadFlow]);

  const handleEntrySearch = () => {
    setEntryPath(entryInput || '/');
  };

  // Build Sankey data — recharts expects nodes+links where link source/target are indices
  const sankeyData = flowData
    ? { nodes: flowData.nodes || [], links: flowData.links || [] }
    : { nodes: [], links: [] };

  return (
    <Box>
      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{
          mb: 3,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
        }}
      >
        <Tab value="pages" label="Pages" />
        <Tab value="flow" label="User Flow (Sankey)" />
      </Tabs>

      {/* ── Pages tab ───────────────────────────────────────────────────── */}
      {subTab === 'pages' && (
        <GlassCard sx={{ p: 3 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ mb: 2, color: isDark ? '#F8FAFC' : '#0F172A' }}
          >
            Page Performance
          </Typography>
          {pagesLoading ? (
            <ShimmerSkeleton height={400} />
          ) : pagesData.length === 0 ? (
            <EmptyState title="No page data" description="Page stats will appear once traffic is recorded." />
          ) : (
            <GlassTable
              columns={[
                { label: 'Path', width: '30%' },
                { label: 'Views', align: 'right' },
                { label: 'Uniques', align: 'right' },
                { label: 'Avg Dwell', align: 'right' },
                { label: 'Bounce', align: 'right' },
                { label: 'Exit', align: 'right' },
              ]}
              sx={{ maxHeight: 480 }}
            >
              {pagesData.map((row, i) => (
                <TableRow key={i}>
                  <TableCell
                    sx={{
                      maxWidth: 240,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                    }}
                  >
                    {row.path_pattern}
                  </TableCell>
                  <TableCell align="right">{(row.views || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{(row.uniques || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{formatDwell(row.avgDwellMs)}</TableCell>
                  <TableCell align="right">{pct(row.bounceRate)}</TableCell>
                  <TableCell align="right">{pct(row.exitRate)}</TableCell>
                </TableRow>
              ))}
            </GlassTable>
          )}
        </GlassCard>
      )}

      {/* ── Flow tab ────────────────────────────────────────────────────── */}
      {subTab === 'flow' && (
        <GlassCard sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: isDark ? '#F8FAFC' : '#0F172A' }}
            >
              User Flow from Entry
            </Typography>
            <TextField
              size="small"
              placeholder="Entry path e.g. /"
              value={entryInput}
              onChange={(e) => setEntryInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleEntrySearch()}
              sx={{ width: 220 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search
                      sx={{ cursor: 'pointer', fontSize: 18, opacity: 0.6 }}
                      onClick={handleEntrySearch}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {flowLoading ? (
            <ShimmerSkeleton height={380} />
          ) : sankeyData.nodes.length === 0 ? (
            <EmptyState
              title="No flow data"
              description="Flow data will appear once the backend tracks navigation sequences."
              iconIndex={2}
            />
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              <Sankey
                data={sankeyData}
                nodePadding={20}
                margin={{ top: 10, right: 120, left: 10, bottom: 10 }}
                link={{ stroke: ADMIN_COLORS.accentCyan, strokeOpacity: 0.3 }}
                node={<SankeyNode theme={theme} />}
              >
                <RechartsTooltip
                  contentStyle={{
                    background: isDark ? 'rgba(15,18,32,0.92)' : '#fff',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.1)'}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
              </Sankey>
            </ResponsiveContainer>
          )}
        </GlassCard>
      )}
    </Box>
  );
}

AnalyticsBehavior.propTypes = {
  filters: PropTypes.object.isRequired,
  apiBase: PropTypes.string,
};
